import { TreeDataNode } from 'antd';
import { ProcessCategory } from 'types/processesTypes';
import { RoleDetail } from 'types/rolesTypes';
import { CompanyUsers } from 'types/userTypes';
import { CategoryResponse, TreeNode } from 'types/productsTypes';
import { TFunction } from 'i18next';
import { translateProductLabel } from 'utils/product';

export function transformProductsToTreeData(
  data: CategoryResponse[],
  t: TFunction
): TreeDataNode[] {
  function generateKeyForLevel(prefix: string, id: string): string {
    return `${prefix}-${id}`;
  }

  function translateTitle(
    type: 'commodity' | 'variety' | 'product',
    translationKey: string,
    fallback: string
  ): string {
    if (t) {
      return translateProductLabel(t, type, translationKey, fallback);
    }
    return fallback;
  }

  function processProducts(products: CategoryResponse[]): TreeDataNode[] {
    return products.map(product => {
      const node: TreeDataNode = {
        title: translateTitle(
          'commodity',
          product.translationKey,
          product.name
        ),
        key: generateKeyForLevel('commodity', product.id),
      };

      const children: TreeDataNode[] = [];

      // Process commodities if they exist (Level 2)
      if (product.varieties && product.varieties.length > 0) {
        product.varieties.forEach(variety => {
          const varietiesNode: TreeDataNode = {
            title: translateTitle(
              'variety',
              variety.translationKey,
              variety.name
            ),
            key: generateKeyForLevel('variety', variety.id),
            children: [],
          };

          // Process types within commodities (Level 3)
          if (variety.products && variety.products.length > 0) {
            variety.products.forEach(product => {
              const productsNode: TreeDataNode = {
                title: translateTitle(
                  'product',
                  product.translationKey,
                  product.name
                ),
                key: generateKeyForLevel('product', product.id),
                children: [],
              };

              // Process products within types (Level 4)
              if (product.finalProducts && product.finalProducts.length > 0) {
                product.finalProducts.forEach(finalProduct => {
                  productsNode.children?.push({
                    title: finalProduct.name,
                    key: generateKeyForLevel('finalProduct', finalProduct.id),
                  });
                });
              }

              varietiesNode.children?.push(productsNode);
            });
          }

          children.push(varietiesNode);
        });
      }

      if (children.length > 0) {
        node.children = children;
      }

      return node;
    });
  }

  // Create the root node "All" that contains all products
  const rootNode: TreeDataNode = {
    title: t('roles.all-products'),
    key: 'all',
    children: processProducts(data),
  };

  return [rootNode];
}

export function transformToFdaProccesTree(
  categories: ProcessCategory[]
): TreeDataNode[] {
  return categories.map(category => ({
    key: category.categoryId,
    title: category.categoryName,
    children: category.processes.map((process: any) => ({
      key: `procces-${process.id}`,
      title: process.name,
    })),
  }));
}

/**
 * Transforms a list of company users into a tree structure suitable for
 * rendering with a tree view component.
 *
 * Each company becomes a node with its users as children.
 * Optionally, a root node titled "All Companies" can be included.
 *
 * @param data - Array of company user objects to be transformed.
 * @param includeAll - If true, includes a root node titled "All Companies".
 *                     Defaults to true.
 * @returns An array of tree nodes representing the companies and their users.
 */
export function transformToCompanyTree(
  data: CompanyUsers[],
  includeAll = true,
  plainIds = false
): TreeDataNode[] {
  function processCompanies(companies: CompanyUsers[]): TreeDataNode[] {
    return companies.map(company => {
      return {
        title: company.name,
        key: plainIds ? company.id : `company-${company.id}`,
        children: company.users.map(user => ({
          title: user.name,
          key: plainIds ? user.id : `user-${user.id}`,
        })),
      };
    });
  }

  if (includeAll) {
    const rootNode: TreeDataNode = {
      title: 'All Companies',
      key: 'all',
      children: processCompanies(data),
    };
    return [rootNode];
  } else {
    return processCompanies(data);
  }
}

export function extractKeysFromProducts(
  products: RoleDetail['products']
): string[] {
  const selectedKeys: string[] = [];
  products.forEach(product => {
    if (product.commodity) {
      selectedKeys.push(`commodity-${product.commodity.id}`);
    }
    if (product.variety) {
      selectedKeys.push(`variety-${product.variety.id}`);
    }
    if (product.product) {
      selectedKeys.push(`product-${product.product.id}`);
    }
    if (product.finalProduct) {
      selectedKeys.push(`finalProduct-${product.finalProduct.id}`);
    }
  });

  return selectedKeys;
}

export function transformToProductTreeData(
  data: CategoryResponse[],
  t: TFunction
): TreeNode[] {
  function processProducts(products: CategoryResponse[]): TreeNode[] {
    return products.map(product => {
      const node: TreeNode = {
        title: translateProductLabel(
          t,
          'commodity',
          product.translationKey,
          product.name
        ),
        key: product.id,
        value: product.id,
        selectable: true,
      };

      const children: TreeNode[] = [];

      if (product.varieties?.length) {
        product.varieties.forEach(variety => {
          const varietyNode: TreeNode = {
            title: translateProductLabel(
              t,
              'variety',
              variety.translationKey,
              variety.name
            ),
            key: variety.id,
            value: variety.id,
            selectable: true,
            children: [],
          };

          if (variety.products?.length) {
            variety.products.forEach(product => {
              const productNode: TreeNode = {
                title: translateProductLabel(
                  t,
                  'product',
                  product.translationKey,
                  product.name
                ),
                key: product.id,
                value: product.id,
                selectable: true,
                children: [],
              };

              if (product.finalProducts?.length) {
                product.finalProducts.forEach(finalProduct => {
                  productNode.children?.push({
                    title: finalProduct.name,
                    key: finalProduct.id,
                    value: finalProduct.id,
                    selectable: true,
                  });
                });
              }

              varietyNode.children?.push(productNode);
            });
          }

          children.push(varietyNode);
        });
      }

      if (children.length) {
        node.children = children;
      }

      return node;
    });
  }

  return processProducts(data);
}
